import type { AuthService } from '../services/AuthService';

function getApiBase(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname !== 'localhost') return '';
  return 'http://localhost:4000';
}

interface AdminUser {
  id: string;
  username: string | null;
  email: string | null;
  role: string;
}

export class AdminPanel {
  private overlay: HTMLElement | null = null;

  constructor(private readonly auth: Pick<AuthService, 'authHeaders'>) {}

  async open(): Promise<void> {
    this.close();
    const users = await this.fetchUsers();
    this.render(users);
  }

  close(): void {
    this.overlay?.remove();
    this.overlay = null;
  }

  private async fetchUsers(): Promise<AdminUser[]> {
    const res = await fetch(`${getApiBase()}/api/admin/users`, { headers: this.auth.authHeaders() });
    if (!res.ok) return [];
    const { users } = await res.json() as { users: AdminUser[] };
    return users;
  }

  private async patchRole(userId: string, role: string): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/users/${userId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...this.auth.authHeaders() },
      body: JSON.stringify({ role }),
    });
    if (!res.ok) {
      this.showError('Action failed — please try again.');
      return;
    }
    await this.refreshTable();
  }

  private async patchUsername(userId: string, username: string): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/users/${userId}/username`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...this.auth.authHeaders() },
      body: JSON.stringify({ username: username || null }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
      this.showError(data.error ?? 'Action failed');
      return;
    }
    await this.refreshTable();
  }

  private async banUser(userId: string): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/ban/${userId}`, {
      method: 'POST',
      headers: this.auth.authHeaders(),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
      this.showError(data.error ?? 'Ban failed');
      return;
    }
    await this.refreshTable();
  }

  private async wipeAll(): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/wipe-all`, {
      method: 'DELETE',
      headers: this.auth.authHeaders(),
    });
    if (!res.ok) {
      this.showError('Wipe failed');
      return;
    }
    const data = await res.json() as { saves: number; leaderboard: number };
    this.showSuccess(`Wiped ${data.saves} save(s) + ${data.leaderboard} leaderboard entries`);
  }

  private async wipeAccounts(): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/wipe-accounts`, {
      method: 'DELETE',
      headers: this.auth.authHeaders(),
    });
    if (!res.ok) {
      this.showError('Wipe failed');
      return;
    }
    const data = await res.json() as { deleted: number };
    this.showSuccess(`${data.deleted} account(s) wiped`);
    await this.refreshTable();
  }

  private async kickAll(): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/kick-all`, {
      method: 'POST',
      headers: this.auth.authHeaders(),
    });
    if (!res.ok) {
      this.showError('Kick failed');
      return;
    }
    this.showSuccess('All players kicked');
  }

  private async wipeCharacters(): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/wipe-characters`, {
      method: 'DELETE',
      headers: this.auth.authHeaders(),
    });
    if (!res.ok) {
      this.showError('Wipe failed');
      return;
    }
    const data = await res.json() as { deleted: number };
    this.showSuccess(`${data.deleted} character save(s) wiped`);
  }

  private async wipePassiveTrees(): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/wipe-passive-trees`, {
      method: 'DELETE',
      headers: this.auth.authHeaders(),
    });
    if (!res.ok) {
      this.showError('Wipe failed');
      return;
    }
    const data = await res.json() as { updated: number };
    this.showSuccess(`${data.updated} passive tree(s) wiped`);
  }

  private async wipeLeaderboard(): Promise<void> {
    const res = await fetch(`${getApiBase()}/api/admin/wipe-leaderboard`, {
      method: 'DELETE',
      headers: this.auth.authHeaders(),
    });
    if (!res.ok) {
      this.showError('Wipe failed');
      return;
    }
    const data = await res.json() as { deleted: number };
    this.showSuccess(`${data.deleted} leaderboard entries wiped`);
  }

  private async refreshTable(): Promise<void> {
    const content = this.overlay?.querySelector('[data-admin-content]') as HTMLElement | null;
    if (!content) return;
    const users = await this.fetchUsers();
    content.innerHTML = this.buildTable(users);
    this.attachTableEvents(content);
  }

  private showError(msg: string): void {
    const content = this.overlay?.querySelector('[data-admin-content]') as HTMLElement | null;
    if (content) content.innerHTML += `<p style="color:#aa5a5a;text-align:center;font-size:11px;margin-top:8px;">${msg}</p>`;
  }

  private showSuccess(msg: string): void {
    const content = this.overlay?.querySelector('[data-admin-content]') as HTMLElement | null;
    if (content) content.innerHTML += `<p style="color:#5aaa5a;text-align:center;font-size:11px;margin-top:8px;">${msg}</p>`;
  }

  private buildTable(users: AdminUser[]): string {
    if (users.length === 0) return '<p style="color:#8a7040;text-align:center;">No users found</p>';
    const rows = users.map(u => {
      const label = u.email ?? u.id;
      const usernameCell = u.username
        ? `<span>${u.username}</span>`
        : `<span style="color:#4a3010;font-style:italic;">—</span>`;
      const editBtn = `<button data-action="edit-username" data-user-id="${u.id}" style="background:#0a0a0e;border:1px solid #3a3a5a;color:#7a7aaa;font-family:Georgia,serif;font-size:9px;padding:2px 6px;cursor:pointer;margin-left:4px;">✎</button>`;

      let actionBtns = '';
      if (u.role === 'banned') {
        actionBtns = `<button data-action="unban" data-user-id="${u.id}" style="background:#0a1a0a;border:1px solid #2a5a2a;color:#5aaa5a;font-family:Georgia,serif;font-size:10px;padding:3px 8px;cursor:pointer;">Unban</button>`;
      } else if (u.role !== 'admin') {
        const approveBtn = u.role === 'pending'
          ? `<button data-action="approve" data-user-id="${u.id}" style="background:#0a1a0a;border:1px solid #2a5a2a;color:#5aaa5a;font-family:Georgia,serif;font-size:10px;padding:3px 8px;cursor:pointer;margin-right:4px;">Approve</button>`
          : '';
        const revokeBtn = u.role !== 'pending'
          ? `<button data-action="revoke" data-user-id="${u.id}" style="background:#1a0a0a;border:1px solid #5a2a2a;color:#aa5a5a;font-family:Georgia,serif;font-size:10px;padding:3px 8px;cursor:pointer;margin-right:4px;">Revoke</button>`
          : '';
        const promoteBtn = `<button data-action="promote" data-user-id="${u.id}" style="background:#0a0a1a;border:1px solid #2a2a7a;color:#5a5aaa;font-family:Georgia,serif;font-size:10px;padding:3px 8px;cursor:pointer;margin-right:4px;">Promote</button>`;
        const banBtn = `<button data-action="ban" data-user-id="${u.id}" style="background:#1a0606;border:1px solid #7a1a1a;color:#e05050;font-family:Georgia,serif;font-size:10px;padding:3px 8px;cursor:pointer;">Ban</button>`;
        actionBtns = `${approveBtn}${revokeBtn}${promoteBtn}${banBtn}`;
      }

      const roleColor = u.role === 'banned' ? '#e05050' : u.role === 'admin' ? '#5a5aaa' : '#8a7040';
      return `<tr>
        <td style="padding:4px 8px;color:#c8973a;font-size:11px;max-width:120px;overflow:hidden;text-overflow:ellipsis;">${usernameCell}${editBtn}</td>
        <td style="padding:4px 8px;color:#c8973a;font-size:11px;max-width:160px;overflow:hidden;text-overflow:ellipsis;">${label}</td>
        <td style="padding:4px 8px;color:${roleColor};font-size:11px;font-weight:bold;">${u.role}</td>
        <td style="padding:4px 8px;">${actionBtns}</td>
      </tr>`;
    }).join('');
    return `<table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="padding:4px 8px;color:#6b4020;font-size:10px;text-align:left;letter-spacing:1px;">USERNAME</th>
        <th style="padding:4px 8px;color:#6b4020;font-size:10px;text-align:left;letter-spacing:1px;">EMAIL</th>
        <th style="padding:4px 8px;color:#6b4020;font-size:10px;text-align:left;letter-spacing:1px;">ROLE</th>
        <th style="padding:4px 8px;color:#6b4020;font-size:10px;text-align:left;letter-spacing:1px;">ACTIONS</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  }

  private attachTableEvents(container: HTMLElement | null): void {
    if (!container) return;
    container.querySelectorAll('button[data-action][data-user-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = (btn as HTMLElement).dataset.action!;
        const userId = (btn as HTMLElement).dataset.userId!;
        if (action === 'edit-username') {
          const newName = prompt('New username (2-24 chars, empty to clear):');
          if (newName === null) return;
          this.patchUsername(userId, newName);
          return;
        }
        if (action === 'ban') {
          if (!confirm('Ban this user? They will be kicked immediately.')) return;
          this.banUser(userId);
          return;
        }
        if (action === 'unban') {
          this.patchRole(userId, 'pending');
          return;
        }
        const roleMap: Record<string, string> = { approve: 'approved', revoke: 'pending', promote: 'admin' };
        this.patchRole(userId, roleMap[action]);
      });
    });
  }

  private render(users: AdminUser[]): void {
    const container = (document.getElementById('game-container') ?? document.body) as HTMLElement;
    this.overlay = document.createElement('div');
    this.overlay.setAttribute('data-admin-panel', '');
    this.overlay.style.cssText =
      'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.85);z-index:10000;';
    this.overlay.innerHTML = `
      <div style="background:#0d0a0e;border:2px solid #5a3600;padding:24px 28px;width:580px;max-height:70vh;overflow-y:auto;box-sizing:border-box;font-family:Georgia,serif;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
          <div style="color:#c8973a;font-size:14px;font-weight:bold;letter-spacing:1px;">✦  Admin Panel  ✦</div>
          <button data-admin-close style="background:none;border:none;color:#4a3010;font-family:Georgia,serif;font-size:12px;cursor:pointer;">✕ Close</button>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px;">
          <button data-admin-kick-all style="background:#0a0a1a;border:1px solid #2a2a7a;color:#5a5aaa;font-family:Georgia,serif;font-size:11px;padding:6px 12px;cursor:pointer;">Kick All Players</button>
          <button data-admin-wipe-all style="background:#1a0606;border:1px solid #7a1a1a;color:#e05050;font-family:Georgia,serif;font-size:11px;padding:6px 12px;cursor:pointer;">Wipe All Data</button>
          <button data-admin-wipe-accounts style="background:#1a0606;border:1px solid #7a1a1a;color:#e05050;font-family:Georgia,serif;font-size:11px;padding:6px 12px;cursor:pointer;">Delete All Accounts</button>
          <button data-admin-wipe-characters style="background:#1a0606;border:1px solid #7a1a1a;color:#e05050;font-family:Georgia,serif;font-size:11px;padding:6px 12px;cursor:pointer;">Wipe All Characters</button>
          <button data-admin-wipe-passive-trees style="background:#1a0a06;border:1px solid #7a4a1a;color:#e0a050;font-family:Georgia,serif;font-size:11px;padding:6px 12px;cursor:pointer;">Wipe All Talent Trees</button>
          <button data-admin-wipe-leaderboard style="background:#1a0a06;border:1px solid #7a4a1a;color:#e0a050;font-family:Georgia,serif;font-size:11px;padding:6px 12px;cursor:pointer;">Wipe Leaderboard</button>
        </div>
        <div data-admin-content>${this.buildTable(users)}</div>
      </div>`;

    container.appendChild(this.overlay);
    this.overlay.querySelector('[data-admin-close]')!.addEventListener('click', () => this.close());
    this.overlay.querySelector('[data-admin-kick-all]')!.addEventListener('click', () => {
      if (!confirm('Kick ALL connected players? They will be forced to re-login.')) return;
      this.kickAll();
    });
    this.overlay.querySelector('[data-admin-wipe-all]')!.addEventListener('click', () => {
      if (!confirm('WIPE ALL DATA: saves, leaderboard, passive trees, AND Hero Armory progression (per-hero level + perks). Accounts are kept. This cannot be undone.')) return;
      this.wipeAll();
    });
    this.overlay.querySelector('[data-admin-wipe-accounts]')!.addEventListener('click', () => {
      if (!confirm('DELETE ALL non-admin accounts, saves, and leaderboard entries? This cannot be undone.')) return;
      this.wipeAccounts();
    });
    this.overlay.querySelector('[data-admin-wipe-characters]')!.addEventListener('click', () => {
      if (!confirm('Delete ALL character saves for ALL users? Account data is preserved. This cannot be undone.')) return;
      this.wipeCharacters();
    });
    this.overlay.querySelector('[data-admin-wipe-passive-trees]')!.addEventListener('click', () => {
      if (!confirm('Clear ALL passive/talent trees for ALL users? This cannot be undone.')) return;
      this.wipePassiveTrees();
    });
    this.overlay.querySelector('[data-admin-wipe-leaderboard]')!.addEventListener('click', () => {
      if (!confirm('Delete ALL leaderboard entries? This cannot be undone.')) return;
      this.wipeLeaderboard();
    });
    this.attachTableEvents(this.overlay.querySelector('[data-admin-content]'));
  }
}
